"use client";

import { ChipOwnProps } from "@mui/material";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import { useTranslations } from "../../translations/useTranslations";
import { getNestedThemeColor } from "../../utils/getNestedThemeColor";
import { StatusKeys, StatusType, statusStyles } from "./statusConfig";
import { StyledBadge, StyledChip } from "./styledStatusComponents";
export type StatusProps = {
  status: StatusKeys;
  kind?: "badge" | "chip" | "icon";
  children?: React.ReactNode;
  ignoreChildren?: boolean;
  sx?: SxProps<Theme>;
  deleteIcon?: ChipOwnProps["deleteIcon"];
  onDelete?: ChipOwnProps["onDelete"];
};

export const Status = ({
  status,
  kind = "chip",
  children,
  sx,
  deleteIcon,
  onDelete,
}: StatusProps) => {
  const { t } = useTranslations("Status");
  const theme = useTheme();

  if (!status || !statusStyles[status]) {
    return null;
  }

  const { color, content: Content, variant } = statusStyles[status];
  const isOutlined = variant === "outlined";
  const isIcon = typeof Content !== "string";
  const nestedColor = getNestedThemeColor(theme, color);

  let contentKey: StatusType = "other";

  switch (kind) {
    case "badge":
      return (
        <StyledBadge
          customColor={color}
          isOutlined={isOutlined}
          badgeContent={isIcon ? <Content color={color} sx={sx} /> : Content}
        >
          {children}
        </StyledBadge>
      );

    case "icon":
      return isIcon ? (
        <Content color={color} sx={{ color: nestedColor, ...sx }} />
      ) : (
        <>{`No icon for ${status}`}</>
      );

    case "chip":
    default:
      if (typeof Content === "string") {
        contentKey = Content as StatusType;
      }

      return (
        <StyledChip
          size="small"
          sx={sx}
          label={isIcon ? null : children ?? t(contentKey)}
          icon={isIcon ? <Content color={color} /> : undefined}
          customColor={color}
          variant={isOutlined ? "outlined" : "filled"}
          deleteIcon={deleteIcon}
          onDelete={onDelete}
        />
      );
  }
};
